import { inject, Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from "@angular/router";
import { Observable, of, forkJoin } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { SnackbarService } from "@core/services/snackbar.service";
import { RutaDinamicaService } from "@core/services/ruta-dinamica.service";
import {
  DashboardService,
  PermissionModulesResponse,
} from "@pages/configuracion/services/dashboard.service";

@Injectable({
  providedIn: "root",
})
export class PermissionGuard {
  private snackbar = inject(SnackbarService);
  private rutaDinamicaService = inject(RutaDinamicaService);
  private router = inject(Router);
  private dashboardService = inject(DashboardService);

  private readonly modulePermissionMap: Record<
    string,
    keyof PermissionModulesResponse
  > = {
    config: "cen",
    lic: "pre",
    vac: "pre",
    tya: "pre",
    haberes: "lh",
    per: "per",
    lh: "lh",
    ml: "rh",
    org: "rh",
    sel: "rh",
    seh: "rh",
  };

  canActivate(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> {
    const menuCache = this.rutaDinamicaService.getMenuUsuario();
    if (menuCache) {
      return this.verifyAccess(menuCache, state.url);
    }

    return this.rutaDinamicaService.fetchMenuUsuario().pipe(
      switchMap((menu) => {
        if (!menu) {
          this.snackbar.error("Error al verificar permisos");
          if (!this.isOnValidPage()) {
            this.router.navigate(["/inicio"]);
          }
          return of(false);
        }
        return this.verifyAccess(menu, state.url);
      }),
    );
  }

  private verifyAccess(menuList: any, url: string): Observable<boolean> {
    // Primero verificar permisos del menú
    const hasMenuPermission = this.checkPermissions(menuList, url);

    if (!hasMenuPermission) {
      return of(false);
    }

    // Luego verificar permisos de módulos
    return this.dashboardService.getPermissionModules().pipe(
      map((modulePermissions) => {
        return this.checkModulePermission(url, modulePermissions);
      }),
    );
  }

  private checkPermissions(menuList: any, url: string): boolean {
    const pathUsuario =
      menuList?.lista.map((menu: any) => this.normalizarPath(menu.path)) ?? [];

    const pathActual = this.normalizarPath(url);

    // Permitir acceso libre al dashboard
    if (
      pathActual === "" ||
      pathActual === "dashboard" ||
      pathActual === "inicio"
    ) {
      return true;
    }

    const tienePermiso = pathUsuario.some((rutaPermitida: string) =>
      pathActual.startsWith(rutaPermitida),
    );

    if (!tienePermiso) {
      this.snackbar.error("Permisos insuficientes para acceder a esta ruta");
      if (!this.isOnValidPage()) {
        this.router.navigate(["/inicio"]);
      }
      return false;
    }

    return true;
  }

  private normalizarPath(path: string): string {
    return path
      .split("?")[0]
      .split(";")[0]
      .replace(/^\/+/, "")
      .replace(/^modulos\//, "")
      .toLowerCase();
  }

  private checkModulePermission(
    url: string,
    modulePermissions: PermissionModulesResponse,
  ): boolean {
    const normalizedPath = this.normalizarPath(url);

    // Permitir acceso libre al dashboard e inicio
    if (
      normalizedPath === "" ||
      normalizedPath === "dashboard" ||
      normalizedPath === "inicio"
    ) {
      return true;
    }

    const pathSegments = normalizedPath.split("/");
    const firstSegment = pathSegments[0];
    let permissionKey: keyof PermissionModulesResponse | undefined;
    permissionKey = this.modulePermissionMap[firstSegment];

    if (!permissionKey && firstSegment.includes("_")) {
      const prefix = firstSegment.split("_")[0];
      permissionKey = this.modulePermissionMap[prefix];
    }
    if (!permissionKey) {
      return true;
    }

    const hasPermission = modulePermissions[permissionKey] === "S";

    if (!hasPermission) {
      this.snackbar.error(`Permisos insuficientes para acceder a esta ruta`);
      if (!this.isOnValidPage()) {
        this.router.navigate(["/inicio"]);
      }
      return false;
    }

    return true;
  }

  private isOnValidPage(): boolean {
    const currentUrl = this.router.url;
    return (
      currentUrl !== "/" &&
      currentUrl !== "" &&
      !currentUrl.startsWith("/login")
    );
  }
}
